/**
 * Máy chủ giả của màn Giá AI (`/api/v1/audit/llm-pricing/*`, ADR-0095 mục 10). Diễn đúng luật ghi của
 * audit-service (`KhoGia.themDongGia`, `KhoGia.themTyGia`): chỉ thêm, dòng đang phủ mốc mới bị đóng tại
 * mốc ấy, dòng mới tự đóng ở mốc bắt đầu của dòng sau nó; dòng cùng mốc bị thay (`[tu, tu)`).
 *
 * Dữ liệu mẫu có: một ô giá có lịch sử ba phiên bản (một đã đóng, một đang chạy, một chưa tới hiệu lực),
 * một dòng bị thay, một bậc giá theo độ dài lời nhắc, một dòng theo tính năng, giá vốn, mẫu rất dài.
 */
import type {
  LlmExchangeRateFetch,
  LlmExchangeRate,
  LlmExchangeRateCreate,
  LlmPrice,
  LlmPriceCreate,
  LocBangGia,
} from '../modules/gia-ai/types';
import { useAuthStore } from '../stores/auth';

/** Người nhập dòng mẫu: mã người dùng, như `created_by` thật (sub của token). Một người khác để thấy cả hai cách hiện. */
const NGUOI = 'u-001';
const NGUOI_KHAC = '7c3e9a52-1b4d-4f6e-9a0b-2d8c5e1f3a77';

/** Dòng thêm trong phiên giả mang mã của chính người đang đăng nhập, như audit-service ghi `sub`. */
const nguoiDangNhap = () => useAuthStore.getState().user?.id ?? null;

function gia(id: number, phu: Partial<LlmPrice> & Pick<LlmPrice, 'model_pattern' | 'effective_from'>): LlmPrice {
  return {
    id,
    price_table: 'ban',
    feature: null,
    provider: 'google',
    currency: 'USD',
    token_unit: 1_000_000,
    input_threshold: null,
    prices: {},
    effective_to: null,
    created_by: NGUOI,
    note: null,
    created_at: phu.effective_from,
    ...phu,
  };
}

let dongGia: LlmPrice[] = [
  gia(1, {
    model_pattern: 'gemini-2.5-pro',
    prices: { dau_vao: '1.500000', dau_ra: '12.000000' },
    effective_from: '2026-06-01T00:00:00.000Z',
    effective_to: '2026-09-01T00:00:00.000Z',
    note: 'Giá khởi điểm khi mở dịch vụ',
  }),
  gia(2, {
    model_pattern: 'gemini-2.5-pro',
    prices: { dau_vao: '1.250000', dau_ra: '10.000000', dau_vao_dem: '0.310000' },
    effective_from: '2026-09-01T00:00:00.000Z',
    effective_to: '2026-12-01T00:00:00.000Z',
    note: 'Google giảm giá từ 01/09',
    created_by: NGUOI_KHAC,
  }),
  gia(3, {
    model_pattern: 'gemini-2.5-pro',
    prices: { dau_vao: '1.100000', dau_ra: '9.000000', dau_vao_dem: '0.275000' },
    effective_from: '2026-12-01T00:00:00.000Z',
    note: 'Bảng giá mới công bố ngày 20/10',
  }),
  gia(4, {
    model_pattern: 'gemini-2.5-pro',
    input_threshold: 200_000,
    prices: { dau_vao: '2.500000', dau_ra: '15.000000' },
    effective_from: '2026-09-01T00:00:00.000Z',
    note: 'Bậc lời nhắc trên 200.000 token',
  }),
  gia(5, {
    model_pattern: 'gemini-2.5-flash*',
    prices: { dau_vao: '0.300000', dau_ra: '2.500000', dau_vao_anh: '0.300000' },
    effective_from: '2026-08-15T00:00:00.000Z',
    effective_to: '2026-08-15T00:00:00.000Z',
    note: 'Nhập nhầm đơn vị, đã thay ngay',
  }),
  gia(6, {
    model_pattern: 'gemini-2.5-flash*',
    prices: { dau_vao: '0.300000', dau_ra: '2.500000' },
    effective_from: '2026-08-15T00:00:00.000Z',
  }),
  gia(7, {
    model_pattern: 'claude-sonnet-4-*',
    provider: 'anthropic',
    prices: { dau_vao: '3', dau_ra: '15', dau_vao_dem: '0.3', ghi_dem: '3.75' },
    effective_from: '2026-07-01T00:00:00.000Z',
  }),
  gia(8, {
    model_pattern: 'qwen3-8b-instruct-vietnamese-finetune-ocr-chung-tu-ke-toan-ban-dia-phuong-*',
    provider: 'foxai',
    currency: 'VND',
    feature: 'ocr.nhan-dang',
    prices: { dau_vao: '5000', dau_ra: '20000' },
    effective_from: '2026-10-01T00:00:00.000Z',
  }),
  gia(9, {
    price_table: 'von',
    model_pattern: 'gemini-2.5-pro',
    prices: { dau_vao: '1.000000', dau_ra: '8.000000' },
    effective_from: '2026-09-01T00:00:00.000Z',
  }),
  gia(10, {
    price_table: 'von',
    model_pattern: 'claude-sonnet-4-*',
    provider: 'anthropic',
    prices: { dau_vao: '2.4', dau_ra: '12' },
    effective_from: '2026-07-01T00:00:00.000Z',
  }),
];

let dongTyGia: LlmExchangeRate[] = [
  {
    id: 1,
    from_currency: 'USD',
    to_currency: 'VND',
    rate: '25100.000000',
    effective_from: '2026-06-01',
    effective_to: '2026-10-01',
    created_by: NGUOI,
    note: 'Vietcombank, bán ra',
    source: 'VIETCOMBANK',
    created_at: '2026-06-01T01:00:00.000Z',
  },
  {
    id: 2,
    from_currency: 'USD',
    to_currency: 'VND',
    rate: '25400.000000',
    effective_from: '2026-10-01',
    effective_to: null,
    created_by: NGUOI,
    note: 'Vietcombank, bán ra',
    source: 'VIETCOMBANK',
    created_at: '2026-10-01T01:00:00.000Z',
  },
  {
    id: 3,
    from_currency: 'EUR',
    to_currency: 'VND',
    rate: '27650.500000',
    effective_from: '2026-10-01',
    effective_to: null,
    created_by: NGUOI_KHAC,
    note: null,
    source: 'THU_CONG',
    created_at: '2026-10-01T01:00:00.000Z',
  },
];

/** `fnmatch` không phân biệt hoa–thường, như `BangGia.khopMau` của audit-service. */
function khopMau(mau: string, moHinh: string): boolean {
  let re = '';
  for (const c of mau.trim().toLowerCase()) {
    if (c === '*') re += '.*';
    else if (c === '?') re += '.';
    else re += c.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  }
  return new RegExp(`^${re}$`, 's').test(moHinh.trim().toLowerCase());
}

const moiTruoc = <T extends { effective_from: string; id: number }>(a: T, b: T) =>
  a.effective_from === b.effective_from ? b.id - a.id : a.effective_from < b.effective_from ? 1 : -1;

const cungO = (a: LlmPrice, b: LlmPriceCreate) =>
  a.price_table === b.price_table &&
  (a.feature ?? null) === (b.feature ?? null) &&
  a.model_pattern === b.model_pattern &&
  (a.input_threshold ?? null) === (b.input_threshold ?? null);

export const mayChuGiaAi = {
  danhSachGia(loc: LocBangGia): { items: LlmPrice[] } {
    const moc = loc.effective_at ? Date.parse(loc.effective_at) : null;
    const items = dongGia
      .filter((d) => !loc.price_table || d.price_table === loc.price_table)
      .filter((d) => !loc.model || khopMau(d.model_pattern, loc.model))
      .filter(
        (d) =>
          moc === null ||
          (Date.parse(d.effective_from) <= moc && (d.effective_to === null || Date.parse(d.effective_to) > moc)),
      )
      .sort(moiTruoc);
    return { items };
  },

  themGia(than: LlmPriceCreate): LlmPrice {
    const tu = new Date(than.effective_from).toISOString();
    const cung = dongGia.filter((d) => cungO(d, than));
    // Dòng cùng mốc bị thay; dòng đang phủ mốc mới bị đóng tại mốc ấy.
    dongGia = dongGia.map((d) =>
      cung.includes(d) && d.effective_from <= tu && (d.effective_to === null || d.effective_to > tu)
        ? { ...d, effective_to: tu }
        : d,
    );
    const sau = cung.filter((d) => d.effective_from > tu).map((d) => d.effective_from).sort()[0] ?? null;
    const moi: LlmPrice = {
      id: Math.max(0, ...dongGia.map((d) => d.id)) + 1,
      price_table: than.price_table,
      feature: than.feature ?? null,
      model_pattern: than.model_pattern,
      provider: than.provider,
      currency: than.currency,
      token_unit: than.token_unit ?? 1_000_000,
      input_threshold: than.input_threshold ?? null,
      prices: { ...than.prices },
      effective_from: tu,
      effective_to: sau,
      created_by: nguoiDangNhap(),
      note: than.note ?? null,
      created_at: new Date().toISOString(),
    };
    dongGia = [...dongGia, moi];
    return moi;
  },

  danhSachTyGia(): { items: LlmExchangeRate[] } {
    return { items: [...dongTyGia].sort(moiTruoc) };
  },

  /** `POST /exchange-rates/sync`: giá bán USD của Vietcombank, không lưu (ADR-0100 mục 6). */
  layTyGiaVietcombank(): LlmExchangeRateFetch {
    const bayGio = new Date();
    return {
      from_currency: 'USD',
      to_currency: 'VND',
      rate: '26020',
      source: 'VIETCOMBANK',
      source_time: new Date(bayGio.getTime() - 25 * 60_000).toISOString(),
      fetched_at: bayGio.toISOString(),
    };
  },

  themTyGia(than: LlmExchangeRateCreate): LlmExchangeRate {
    const tu = than.effective_from;
    const cung = dongTyGia.filter((d) => d.from_currency === than.from_currency && d.to_currency === than.to_currency);
    dongTyGia = dongTyGia.map((d) =>
      cung.includes(d) && d.effective_from <= tu && (d.effective_to === null || d.effective_to > tu)
        ? { ...d, effective_to: tu }
        : d,
    );
    const sau = cung.filter((d) => d.effective_from > tu).map((d) => d.effective_from).sort()[0] ?? null;
    const moi: LlmExchangeRate = {
      id: Math.max(0, ...dongTyGia.map((d) => d.id)) + 1,
      from_currency: than.from_currency,
      to_currency: than.to_currency,
      rate: than.rate,
      effective_from: tu,
      effective_to: sau,
      created_by: nguoiDangNhap(),
      note: than.note ?? null,
      source: than.source ?? 'THU_CONG',
      created_at: new Date().toISOString(),
    };
    dongTyGia = [...dongTyGia, moi];
    return moi;
  },
};
